/**
 * Module Administration (bible §18) — contrat partagé.
 * Les secrets ne traversent JAMAIS cette frontière (§18.2).
 */
import { z } from "zod";
import { ReglageValidationSchema } from "./validation.js";
import { PauseSaisieSchema } from "./pauses";
import { SanteItemSchema } from "./pilotage";

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** États de fraîcheur §3.5. */
export const ConnecteurSchema = z.object({
  id: z.string(),
  nom: z.string(),
  etat: z.enum(["a_jour", "retarde", "obsolete", "indisponible", "non_configure"]),
  detail: z.string(), // lisible : pourquoi cet état (§18.5 : diagnostic < 2 min)
  compte: z.string().nullable(),
  derniereSync: z.string().datetime({ offset: true }).nullable(),
  derniereErreur: z.string().nullable(),
});
export type Connecteur = z.infer<typeof ConnecteurSchema>;

export const FlagAdminSchema = z.object({
  id: z.string(),
  actif: z.boolean(),
  defaut: z.boolean(),
  description: z.string(),
  motif: z.string().nullable(),
  auteur: z.string().nullable(),
  /**
   * `false` = le drapeau est déclaré mais AUCUN code ne le lit : le basculer
   * ne change rien. Mesuré le 12/08 — sept des dix drapeaux étaient dans ce
   * cas. Un interrupteur qui ne commande rien est pire qu'un interrupteur
   * absent : on croit avoir agi. `.default(true)` pour rester compatible avec
   * une réponse d'un serveur antérieur.
   */
  branche: z.boolean().default(true),
  /**
   * Volet santé du triptyque autorisé / branché / sain (sprint 2, lot A) :
   * l'état agrégé des capacités de la sonde liées à ce drapeau. `null` =
   * « non mesuré » — JAMAIS affiché « sain » : un vert sans mesure est le
   * scénario des 33 jours de copie muette. `.default(null)` pour rester
   * compatible avec une réponse d'un serveur antérieur.
   */
  sante: z
    .object({ etat: z.enum(["ok", "degrade", "hs"]), detail: z.string() })
    .nullable()
    .default(null),
});
export type FlagAdmin = z.infer<typeof FlagAdminSchema>;

export const AdminResponseSchema = z.object({
  connecteurs: z.array(ConnecteurSchema),
  flags: z.array(FlagAdminSchema),
  fermetures: z.array(z.object({ jour: IsoDate, motif: z.string(), auteur: z.string() })),
  // Reglages de validation par type de decision (cadrage Dove 2026-08-05).
  reglagesValidation: z.array(ReglageValidationSchema),
  /**
   * Pauses de saisie Vevidental — distinctes des fermetures : le laboratoire
   * travaille, seule la saisie est suspendue (voir pauses.ts).
   */
  pauses: z.array(PauseSaisieSchema),
  /** allowlist EFFECTIVE (lecture) — la modification reste le fichier versionné roles.ts. */
  utilisateurs: z.array(
    z.object({ email: z.string(), roles: z.array(z.string()), permissions: z.array(z.string()) }),
  ),
  version: z.object({ app: z.string(), node: z.string(), environnement: z.string() }),
  /**
   * Silence de la réception (§17.5, §18.2) — même mesure et même état que
   * l'ancienne carte « Santé des connexions » du tableau de bord, désormais
   * ici seulement. `null` = aucun travail mesuré (rôle non admin exclu en amont).
   */
  receptionTravaux: SanteItemSchema.nullable().default(null),
  /**
   * Mesure d'adoption (C4, critère J+30) — une ligne par e-mail de l'allowlist
   * (`ROLE_MAP`). `comprisLe` et `joursActifs30j` viennent du journal d'audit,
   * jamais recalculés côté front. `.default([])` pour rester compatible avec
   * une réponse d'un serveur antérieur.
   */
  adoption: z
    .array(z.object({ email: z.string(), comprisLe: z.string().nullable(), joursActifs30j: z.number().int().min(0) }))
    .default([]),
  /**
   * `null` = mesure prise normalement. Un journal d'audit illisible n'est PAS
   * un journal vide (voir `pilotage/adoption.ts`) : `adoption` remonte alors
   * vide et CE champ nomme la panne — jamais un zéro qui ment en silence.
   * `.default(null)` pour rester compatible avec une réponse d'un serveur
   * antérieur.
   */
  adoptionErreur: z.string().nullable().default(null),
});
export type AdminResponse = z.infer<typeof AdminResponseSchema>;

/**
 * Un réglage texte de l'administration (spec 11/09/2026, table `reglage`).
 * Premier usage : `attentes_destinataire_test` — `valeur: null` = pas de
 * remplacement, les mails d'attente partent vraiment aux cabinets.
 */
export const ReglageSchema = z.object({ cle: z.string(), valeur: z.string().nullable() });
export type Reglage = z.infer<typeof ReglageSchema>;

export const AuditLineSchema = z.object({
  ts: z.string(),
  level: z.string(),
  email: z.string().nullable(),
  role: z.string().nullable(),
  method: z.string(),
  path: z.string(),
  status: z.number(),
  durMs: z.number(),
});
export const AuditResponseSchema = z.object({ lignes: z.array(AuditLineSchema), fichier: z.string() });
export type AuditResponse = z.infer<typeof AuditResponseSchema>;
